import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageUp, Trash2 } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { useCenterLogo, Center } from "../../../center";

const ACCEPTED = "image/png,image/jpeg,image/webp";
const MAX_BYTES = 5 * 1024 * 1024;

interface Props {
    center: Center;
}

/**
 * The centre's logo: shown in the emails its therapists send to its patients.
 *
 * Saved as soon as it is chosen, apart from the details form: a file is not a
 * field someone expects to lose by leaving without pressing "Save".
 */
export default function CenterLogoField({ center }: Props) {
    const { t } = useTranslation();
    const { upload, remove } = useCenterLogo(center.id);
    const input = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string | null>(null);

    const busy = upload.isPending || remove.isPending;

    const choose = async (file: File | undefined) => {
        if (!file) return;
        setError(null);
        if (file.size > MAX_BYTES) {
            setError(t("center.logo.tooLarge"));
            return;
        }
        try {
            await upload.mutateAsync(file);
        } catch {
            setError(t("center.logo.rejected"));
        } finally {
            if (input.current) input.current.value = "";
        }
    };

    const clear = async () => {
        setError(null);
        try {
            await remove.mutateAsync();
        } catch {
            setError(t("center.logo.rejected"));
        }
    };

    return (
        <div className="sm:col-span-2">
            <span className="mb-1.5 block text-sm font-medium text-content-secondary">
                {t("center.logo.label")}
            </span>
            <div className="flex flex-col gap-4 rounded-xl border border-dashed border-gray-300 p-4 dark:border-gray-700 sm:flex-row sm:items-center">
                <div className="flex h-20 w-full shrink-0 items-center justify-center rounded-lg bg-surface-sunken px-3 sm:w-48">
                    {center.logo_url ? (
                        <img
                            src={center.logo_url}
                            alt={t("center.logo.alt", { center: center.name })}
                            className="max-h-16 max-w-full object-contain"
                        />
                    ) : (
                        <span className="text-xs text-content-tertiary">
                            {t("center.logo.none")}
                        </span>
                    )}
                </div>
                <div className="flex flex-1 flex-col gap-2">
                    <p className="text-sm text-content-secondary">{t("center.logo.hint")}</p>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => input.current?.click()}
                            startIcon={<ImageUp className="h-4 w-4" />}
                        >
                            {center.logo_url ? t("center.logo.replace") : t("center.logo.upload")}
                        </Button>
                        {center.logo_url && (
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={busy}
                                onClick={clear}
                                startIcon={<Trash2 className="h-4 w-4" />}
                            >
                                {t("center.logo.remove")}
                            </Button>
                        )}
                    </div>
                    {error && (
                        <p role="alert" className="text-sm text-status-critical">
                            {error}
                        </p>
                    )}
                </div>
                <input
                    ref={input}
                    type="file"
                    accept={ACCEPTED}
                    className="hidden"
                    data-testid="center-logo-input"
                    onChange={(event) => choose(event.target.files?.[0])}
                />
            </div>
        </div>
    );
}
